import moment from 'moment'
import * as React from 'react'
import { connect } from 'react-redux'
import { ActionIntegration } from '../../../domain/ActionIntegration'
import SymfonyLink from '../../../helper/link'
import { ServerCapability } from '../../../helper/ServerCapability'
import { ApplicationState } from '../../../redux/ApplicationState'
import { getActiveTracepointIntegrations } from '../../../redux/V2/ActionIntegrations/ActionIntegrationState'
import { fetchActionIntegrationsForTracepoints } from '../../../redux/V2/ActionIntegrations/actions'
import { Col, Row } from '../../../ui/grid'
import { parseTransactionName } from '../../../ui/transactionName'
import TracepointFormHeader from './tracepoint-form-header'
import { ReactNode } from 'react'

interface OwnProps {
    transaction: string
    allowedDurations: number[]
    showNotifyOnFirstSlow: boolean
    annotationDefaults?: { [key: string]: string | number }
    onSubmit: (state: TracepointFormState) => Promise<void>
    renderFormActions: (isValid: boolean) => ReactNode
    initialState?: TracepointFormState
}

interface StateProps {
    customAnnotations: string[]
    environment: string
    service: string
    integrations: ActionIntegration[]
}

interface DispatchProps {
    fetchActionIntegrationsForTracepoints: (environment: string, service: string) => void
}

export enum TracepointRuleFilterThresholdType {
    TIME_MS,
    MEMORY_MB,
}

type Props = StateProps & DispatchProps & OwnProps

export interface TracepointAnnotationFormState {
    name: string
    value: string
}

export interface TracepointFormState {
    annotations: TracepointAnnotationFormState[]
    duration: number
    filterThresholdMs: number
    filterThresholdMemoryMb: number
    notifyOnFirst: boolean
    notifyOnFirstSlow: boolean
    notifyOnFirstSlowThreshold: number
    notifyOnCompletion: boolean
    enableCallgraph: boolean
    callgraphDuration: number
    notificationIntegrations: number[]
    addAnnotationKey?: string
    addAnnotationValue?: string
    filterThresholdType: TracepointRuleFilterThresholdType
}

class TracepointFormFields extends React.Component<Props> {
    public static defaultProps = {
        annotationDefaults: {},
        showNotifyOnFirstSlow: false,
    }

    public state: TracepointFormState = {
        annotations: [],
        duration: 15,
        filterThresholdMs: 0,
        filterThresholdMemoryMb: 0,
        notifyOnFirst: false,
        notifyOnFirstSlow: false,
        notifyOnFirstSlowThreshold: 0,
        notifyOnCompletion: true,
        notificationIntegrations: [],
        enableCallgraph: true,
        callgraphDuration: 2,
        addAnnotationKey: '',
        addAnnotationValue: '',
        filterThresholdType: TracepointRuleFilterThresholdType.TIME_MS,
    }

    public constructor(props: Props) {
        super(props)

        if (props.initialState) {
            this.state = {
                ...this.state,
                ...this.props.initialState,
            }
        }
    }

    private styleInlineInputs = { display: 'inline-block', width: 'auto' }
    private allowedAnnotations = {
        'http.host': { placeholder: 'e.g. www.example.com without scheme' },
        'http.url': { placeholder: 'e.g. /example/path without the host' },
        'http.status': { placeholder: 'e.g. 200, 301, 401, 500' },
        'http.method': { placeholder: 'e.g. POST, GET, PUT' },
    }

    public componentDidMount = (): void => {
        this.props.fetchActionIntegrationsForTracepoints(this.props.environment, this.props.service)
    }

    public render = (): React.ReactNode => {
        const unusedAnnotations = Object.keys(this.allowedAnnotations).filter(annotation => !this.state.annotations[annotation])
        const unusedCustomAnnotations = this.props.customAnnotations.slice(0, 100).filter(annotation => !this.state.annotations[annotation])
        const { transaction } = this.props
        const { tag: transactionTag } = parseTransactionName(transaction)

        return (
            <form name="tracepoint-form" className="tracepoint-form" onSubmit={ev => this.submit(ev)}>
                <TracepointFormHeader transaction={transaction} />
                {transactionTag ? (
                    <Row>
                        <Col width={3}>
                            <label className="control-label tw-ml-8">Tagged with</label>
                        </Col>
                        <Col width={9}>
                            <span className="label label-outline-default">{transactionTag}</span>
                        </Col>
                    </Row>
                ) : null}

                <hr className="tw-mt-8 tw-mb-8 tw-bg-grey-50 tw-color-grey-50" style={{ height: 2 }} />

                <Row>
                    <Col width={3}>
                        <label className="control-label tw-ml-8">Settings</label>
                    </Col>
                    <Col width={9}>
                        <div className="form-group">
                            <label htmlFor="tracepointform_duration" className="control-label">
                                Duration
                            </label>
                            <div className="input-group">
                                <select
                                    name="duration"
                                    id="tracepointform_duration"
                                    className="form-control"
                                    style={this.styleInlineInputs}
                                    onChange={ev => this.change('duration', ev)}
                                    value={this.state.duration}
                                >
                                    {this.props.allowedDurations.map(minutes => {
                                        return (
                                            <option value={minutes} key={minutes}>
                                                {minutes < 60 ? minutes + ' minutes' : minutes == 60 ? '1 hour' : minutes / 60 + ' hours'}
                                            </option>
                                        )
                                    })}
                                </select>
                                <span className="input-group-addon"> until {this.activeUntil()}</span>
                            </div>
                        </div>
                        {this.state.filterThresholdType === TracepointRuleFilterThresholdType.TIME_MS ? (
                            <div className="form-group">
                                <label htmlFor="tracepointform_filterthresholdms" className="control-label">
                                    Response Time
                                </label>
                                <ServerCapability capability="TracepointMemory" hideChildrenOnCapabilityFail>
                                    <span
                                        onClick={ev => {
                                            ev.preventDefault()
                                            this.setState({
                                                filterThresholdType: TracepointRuleFilterThresholdType.MEMORY_MB,
                                                filterThresholdMs: 0,
                                            })
                                        }}
                                        className="text-mute tw-ml-4"
                                        style={{ display: 'inline-block', cursor: 'pointer' }}
                                    >
                                        (Switch to Memory)
                                    </span>
                                </ServerCapability>
                                <div className="input-group">
                                    <input
                                        name="filterthresholdms"
                                        id="tracepointform_filterthresholdms"
                                        className="input-sm form-control text-right"
                                        style={{ ...this.styleInlineInputs, width: 60 }}
                                        type="number"
                                        value={this.state.filterThresholdMs}
                                        onChange={ev => this.change('filterThresholdMs', ev, true)}
                                    />
                                    <span className="input-group-addon">
                                        <strong>ms</strong> or slower
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="form-group">
                                <label htmlFor="tracepointform_filterthresholdmemorymb" className="control-label">
                                    Memory Usage
                                </label>
                                <span
                                    onClick={ev => {
                                        ev.preventDefault()
                                        this.setState({
                                            filterThresholdType: TracepointRuleFilterThresholdType.TIME_MS,
                                            filterThresholdMemoryMb: 0,
                                        })
                                    }}
                                    className="text-mute tw-ml-4"
                                    style={{ display: 'inline-block', cursor: 'pointer' }}
                                >
                                    (Switch to Time)
                                </span>
                                <div className="input-group">
                                    <input
                                        name="filterthresholdmemorymb"
                                        id="tracepointform_filterthresholdmemorymb"
                                        className="input-sm form-control text-right"
                                        style={{ ...this.styleInlineInputs, width: 60 }}
                                        type="number"
                                        value={this.state.filterThresholdMemoryMb}
                                        onChange={ev => this.change('filterThresholdMemoryMb', ev, true)}
                                    />
                                    <span className="input-group-addon">
                                        <strong>MB</strong> or higher
                                    </span>
                                </div>
                            </div>
                        )}
                        <div className="form-group">
                            <label className="control-label">Additional Filters</label>
                            {this.state.annotations.map(annotation => (
                                <div key={annotation.name} className="form-control" style={{ width: '90%', marginBottom: '0.5em' }}>
                                    <strong>{annotation.name}</strong>&nbsp;
                                    {annotation.value}
                                    <a className="pull-right" onClick={ev => this.removeAnnotation(ev, annotation.name)}>
                                        x
                                    </a>
                                </div>
                            ))}
                            <div className="input-group">
                                <div className="input-group-btn">
                                    <button type="button" className="btn btn-default dropdown-toggle" data-toggle="dropdown">
                                        {this.state.addAnnotationKey || 'Field'} <span className="caret" />
                                    </button>
                                    <ul className="dropdown-menu">
                                        {unusedAnnotations.map(value => (
                                            <li key={value}>
                                                <a
                                                    onClick={ev => {
                                                        ev.preventDefault()
                                                        this.setState({
                                                            addAnnotationKey: value,
                                                            addAnnotationValue: this.annotationDefault(value),
                                                        })
                                                    }}
                                                >
                                                    {value}
                                                </a>
                                            </li>
                                        ))}

                                        <li className="dropdown-divider" />
                                        <li className="dropdown-header">Custom Annotations</li>
                                        {this.props.customAnnotations.length > 0 ? null : (
                                            <li>
                                                <a
                                                    href="https://support.tideways.com/article/198-custom-metadata?utm_medium=app&utm_source=tracepoint-form&utm_campaign=custom-metadata"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <i className="fa fa-question-circle" /> Add Custom Annotations
                                                </a>
                                            </li>
                                        )}
                                        {unusedCustomAnnotations.map(value => (
                                            <li key={value}>
                                                <a
                                                    onClick={ev => {
                                                        ev.preventDefault()
                                                        this.setState({
                                                            addAnnotationKey: value,
                                                            addAnnotationValue: this.annotationDefault(value),
                                                        })
                                                    }}
                                                >
                                                    {value}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <input
                                    type="text"
                                    value={this.state.addAnnotationValue}
                                    style={{
                                        ...this.styleInlineInputs,
                                        minWidth: 300,
                                    }}
                                    placeholder={this.annotationPlaceholder(this.state.addAnnotationKey)}
                                    className="form-control"
                                    onChange={ev => this.change('addAnnotationValue', ev)}
                                />
                                {!this.state.addAnnotationKey ? null : (
                                    <React.Fragment>
                                        <span className="input-group-btn">
                                            <button
                                                disabled={!this.addAnnotationValid()}
                                                className="btn btn-primary"
                                                onClick={ev => this.addAnnotation(ev)}
                                            >
                                                add
                                            </button>
                                        </span>
                                        <span className="input-group-btn">
                                            <button
                                                className="btn-default btn"
                                                onClick={ev => {
                                                    ev.preventDefault()
                                                    this.setState({ addAnnotationKey: '', addAnnotationValue: '' })
                                                }}
                                            >
                                                cancel
                                            </button>
                                        </span>
                                    </React.Fragment>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>

                <React.Fragment>
                    <hr className="tw-mt-8 tw-mb-8 tw-bg-grey-50 tw-color-grey-50" style={{ height: 2 }} />
                    <Row>
                        <Col width={3}>
                            <label className="control-label tw-ml-8">Callgraphs</label>
                        </Col>
                        <Col width={9}>
                            <ServerCapability
                                capability="CallgraphBoosting"
                                level="info"
                                expanded={false}
                                failOnMissingCapabilities
                                checkForActivation
                                noMargin
                            >
                                <div className="form-group" style={{ margin: 0 }}>
                                    <div className="checkbox checkbox-primary" style={{ display: 'inline-block' }}>
                                        <input
                                            type="checkbox"
                                            name="enableCallgraph"
                                            id="tracepointform_enableCallgraph"
                                            style={{ top: '20px' }}
                                            checked={this.state.enableCallgraph}
                                            onChange={ev => this.changeCheckbox('enableCallgraph', ev)}
                                        />
                                        <label htmlFor="tracepointform_enableCallgraph">Activate Callgraph Profiler</label>
                                    </div>
                                </div>
                            </ServerCapability>
                        </Col>
                    </Row>
                </React.Fragment>

                <hr className="tw-mt-8 tw-mb-8 tw-bg-grey-50 tw-color-grey-50" style={{ height: 2 }} />

                <Row>
                    <Col width={3}>
                        <label className="control-label tw-ml-8">Notifications</label>
                    </Col>
                    <Col width={9}>
                        <div className="form-group">
                            <div className="checkbox checkbox-primary">
                                <input
                                    type="checkbox"
                                    name="notifyOnCompletion"
                                    id="tracepointform_notifyOnCompletion"
                                    checked={this.state.notifyOnCompletion}
                                    onChange={ev => this.changeCheckbox('notifyOnCompletion', ev)}
                                />
                                <label htmlFor="tracepointform_notifyOnCompletion">at the end of duration with a summary report</label>
                            </div>
                        </div>

                        <div className="form-group">
                            <div className="checkbox checkbox-primary">
                                <input
                                    type="checkbox"
                                    name="notifyOnFirst"
                                    id="tracepointform_notifyOnFirst"
                                    checked={this.state.notifyOnFirst}
                                    onChange={ev => this.changeCheckbox('notifyOnFirst', ev)}
                                />
                                <label htmlFor="tracepointform_notifyOnFirst">when first trace is collected</label>
                            </div>
                        </div>

                        {this.props.showNotifyOnFirstSlow && (
                            <div className="form-group">
                                <div className={this.notifyOnSlowValid() ? 'form-group' : 'form-group has-error'} style={{ margin: 0 }}>
                                    <div className="checkbox checkbox-primary" style={{ display: 'inline-block' }}>
                                        <input
                                            type="checkbox"
                                            name="notifyOnFirstSlow"
                                            id="tracepointform_notifyOnFirstSlow"
                                            checked={this.state.notifyOnFirstSlow}
                                            onChange={ev => this.changeCheckbox('notifyOnFirstSlow', ev)}
                                        />
                                        <label htmlFor="tracepointform_notifyOnFirstSlow">when first trace is collected slower than</label>
                                    </div>
                                    <div style={{ display: 'inline-block' }}>
                                        &nbsp; &nbsp; &nbsp;
                                        <input
                                            name="notifyOnFirstSlowThreshold"
                                            id="tracepointform_notifyOnFirstSlowThreshold"
                                            className="input-sm form-control text-right"
                                            style={{ ...this.styleInlineInputs, width: 60 }}
                                            type="number"
                                            value={this.state.notifyOnFirstSlowThreshold}
                                            onChange={ev => this.change('notifyOnFirstSlowThreshold', ev)}
                                        />
                                        &nbsp;
                                        <strong>ms</strong>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="form-group tw-mr-8" style={{ marginBottom: 0 }}>
                            <label>
                                Notify by{' '}
                                <SymfonyLink
                                    className="text-muted tw-font-weight-normal"
                                    title="configure notifications"
                                    route="XhprofAlertBundle.Configuration.list"
                                >
                                    <i className="fa fa-cog text-muted" />
                                </SymfonyLink>
                            </label>
                            <ul className="list-inline tw-ml-8" style={{ display: 'inline-block' }}>
                                <li>
                                    <i className="text-muted fa fa-envelope" /> personal e-mail
                                </li>
                                {this.props.integrations.map(integration => (
                                    <li key={integration.id}>
                                        <i className={'text-muted fa ' + integration.icon} /> {integration.title}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Col>
                </Row>

                <div className="centered tw-p-8 tw-bg-grey-50">{this.props.renderFormActions(this.valid())}</div>
            </form>
        )
    }

    private change = (field: string, ev: React.ChangeEvent<any>, ensureNumber: boolean = false) => {
        let { value } = ev.target

        if (ensureNumber) {
            if (isNaN(value) || !value) {
                value = 0
            }
        }

        this.setState({ [field]: value })
    }

    private changeCheckbox = (field: string, ev: React.ChangeEvent<HTMLInputElement>) => this.setState({ [field]: ev.target.checked })

    private submit = async (ev: React.FormEvent<HTMLFormElement>) => {
        ev.preventDefault()

        // omit temp state variables
        const { addAnnotationKey, addAnnotationValue, ...state } = this.state

        this.props.onSubmit(state)
    }

    private valid = (): boolean => this.notifyOnSlowValid() && !this.state.addAnnotationKey

    private notifyOnSlowValid = (): boolean => !this.state.notifyOnFirstSlow || this.state.notifyOnFirstSlowThreshold > 0

    private activeUntil = (): string => {
        const start = moment.utc()

        start.add(this.state.duration, 'minutes')

        return start.local().format('HH:mm')
    }

    private addAnnotation = ev => {
        if (!this.state.addAnnotationKey || !this.state.addAnnotationValue) {
            return
        }

        ev.preventDefault()
        this.setState({
            annotations: [...this.state.annotations, { name: this.state.addAnnotationKey, value: this.state.addAnnotationValue }],
            addAnnotationKey: '',
            addAnnotationValue: '',
        })
    }

    private removeAnnotation = (ev, annotation) => {
        ev.preventDefault()

        const annotations = this.state.annotations.filter(a => a.name !== annotation)

        this.setState({
            annotations,
        })
    }

    private addAnnotationValid = () => {
        return this.state.addAnnotationKey !== '' && this.state.addAnnotationValue !== ''
    }

    private annotationPlaceholder = (key: string | undefined): string => {
        if (!key) {
            return 'case-insensitive, wildcard * + ? allowed'
        }

        return this.allowedAnnotations[key] ? this.allowedAnnotations[key].placeholder : 'custom annotation'
    }

    private annotationDefault = (value: string): string => {
        if (!this.props.annotationDefaults) {
            return ''
        }

        return '' + (this.props.annotationDefaults[value] || '')
    }
}

export default connect<StateProps, DispatchProps>(
    (globalState: ApplicationState, props: Props): StateProps => {
        const { environment, service } = globalState.tidewaysContext

        return {
            customAnnotations: globalState.tidewaysContext.customAnnotations || [],
            environment,
            service,
            integrations: getActiveTracepointIntegrations(globalState, environment, service),
        }
    },
    {
        fetchActionIntegrationsForTracepoints,
    },
)(TracepointFormFields)
